import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Result, Spin, Typography } from 'antd';
import dayjs from 'dayjs';

import TrackerLogo from 'components/_shared/TrackerLogo/TrackerLogo';
import TimeSheet from 'components/TimeSheet/TimeSheet';
import { getSharedTimesheetInfo } from 'api/TimesheetShare/requests';
import { SharedTimesheetInfo } from 'api/TimesheetShare/types';
import { UserDetails } from 'api/User/types';
import { FULL_MONTH_YEAR_FORMAT } from 'helpers/constants/_common/timeFormats';

import S from './helpers/styles.module.sass';

/**
 * Public read-only page behind a share link. Rendered outside of AppAuthWrapper,
 * so it never redirects to the login page.
 */
const SharedTimesheet = () => {
  const { token } = useParams<{ token: string }>();

  const [ info, setInfo ] = useState<SharedTimesheetInfo | null>(null);
  const [ isLoading, setLoading ] = useState(true);
  const [ isInvalid, setInvalid ] = useState(false);

  useEffect(() => {
    getSharedTimesheetInfo(token)
      .then(setInfo)
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [ token ]);

  if (isLoading) {
    return (
      <div className={S.centered}>
        <Spin size="large" />
      </div>
    );
  }

  if (isInvalid || !info) {
    return (
      <div className={S.centered}>
        <Result
          status="404"
          title="Link is not available"
          subTitle="This sharing link has expired or does not exist. Ask for a new one."
        />
      </div>
    );
  }

  // TimeSheet expects a full user object, but a link holder is only ever given a name.
  const sharedUser = {
    userId: info.user.userId,
    forename: info.user.forename,
    surname: info.user.surname,
    avatarUrl: info.user.avatarUrl,
  } as UserDetails;

  const renderLegend = () => {
    if (!info.legend.length) {
      return <span className={S.legendEmpty}>No hours reported in this month</span>;
    }

    return info.legend.map(partner => (
      <span key={partner.partnerId} className={S.legendItem}>
        <i className={S.legendColor} style={{ backgroundColor: partner.color }} />
        {partner.name}
      </span>
    ));
  };

  return (
    <div className={S.sharedPage}>
      <div className={S.header}>
        <TrackerLogo className={S.logo} />

        <div className={S.headerText}>
          <Typography.Title level={4} className={S.title}>
            {`${info.user.forename} ${info.user.surname}`}
            {' — '}
            {dayjs(`${info.year}-${info.month}-1`).format(FULL_MONTH_YEAR_FORMAT)}
          </Typography.Title>
          <span className={S.subtitle}>
            Read-only view · link expires
            {' '}
            {dayjs(info.expirationDate).format('DD MMM YYYY, HH:mm')}
          </span>
        </div>
      </div>

      <div className={S.legend}>
        <span className={S.legendLabel}>Projects:</span>
        {renderLegend()}
      </div>

      <div className={S.timesheetWrapper}>
        <TimeSheet user={sharedUser} sharedToken={token} curatorOnlyView />
      </div>
    </div>
  );
};

export default SharedTimesheet;
